// src/components/topNavBar/topNavBar.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AutoComplete, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { FaBell, FaUserCircle, FaChevronDown, FaSignOutAlt } from "react-icons/fa";
import axios from "../../services/axiosConfig";
import API_ENDPOINTS from "../../services/apiEndpoints";
import { getAuthToken } from "../../auth/auth";
import { getCurrentUser, signOut } from "aws-amplify/auth";
import { getAllowedNavItems } from "../../utils/navItems";
import "./topNavBar.css";

function TopNavBar({ title }) {
  const navigate = useNavigate();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchOptions, setSearchOptions] = useState([]);
  const [userDetails, setUserDetails] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    dateOfBirth: "",
    role: "",
    loading: true
  });
  const dropdownRef = useRef(null);

  // Build search suggestions by filtering allowed nav items against the typed value
  const handleSearch = (value) => {
    setSearchValue(value);
    if (!value.trim()) {
      setSearchOptions([]);
      return;
    }
    const lower = value.toLowerCase();
    const allowed = getAllowedNavItems();
    const matches = allowed.filter((item) => item.label.toLowerCase().includes(lower) || item.breadcrumb.toLowerCase().includes(lower));
    setSearchOptions(
      matches.map((item) => ({
        value: item.path, // used internally for navigation
        // what the user sees in the dropdown
        label: (
          <div className="search-suggestion">
            <span className="search-suggestion-icon">{item.icon}</span>
            <span className="search-suggestion-breadcrumb">{item.breadcrumb}</span>
          </div>
        )
      }))
    );
  };

  // Navigate to the selected page and clear the search box
  const handleSelect = (path) => {
    setSearchValue("");
    setSearchOptions([]);
    navigate(path);
  };

  // Fetch current user details
  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const currentUser = await getCurrentUser();
        const userId = currentUser.userId;
        const email = currentUser.signInDetails?.loginId || "N/A";

        const token = await getAuthToken();
        const response = await axios.get(`${API_ENDPOINTS.USERS}?status=signedup`, { headers: { Authorization: `Bearer ${token}` } });

        const userData = response.data.users.find((user) => user.userId === userId);

        if (userData) {
          setUserDetails({
            name: userData.name || "N/A",
            email: email,
            phoneNumber: userData.phoneNumber || "",
            dateOfBirth: userData.dateOfBirth || "Not provided",
            role: userData.role || "No role assigned",
            loading: false
          });
        } else {
          setUserDetails({
            name: "Unknown User",
            email: email,
            phoneNumber: "",
            dateOfBirth: "Not provided",
            role: "No role assigned",
            loading: false
          });
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
        setUserDetails({
          name: "Error loading",
          email: "Error loading",
          phoneNumber: "",
          dateOfBirth: "Not available",
          role: "Not available",
          loading: false
        });
      }
    };

    fetchUserDetails();
  }, []);

  // Close profile dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const toggleProfileDropdown = () => {
    setShowProfileDropdown(!showProfileDropdown);
  };

  return (
    <div className="top-navbar">
      <div className="nav-page-title-wrap">
        <h2 className="nav-page-title">{title}</h2>
      </div>

      <div className="nav-right">
        {/* Search bar — Ant Design AutoComplete replaces the old custom div */}
        <AutoComplete
          options={searchOptions}
          onSearch={handleSearch}
          onSelect={handleSelect}
          value={searchValue}
          popupClassName="search-dropdown-popup"
          // Keyboard nav (arrow keys, Enter, Escape) is built into AutoComplete
        >
          <Input
            placeholder="Search for a page..."
            prefix={<SearchOutlined className="search-icon" />}
            className="search-input"
            allowClear
            onClear={() => {
              setSearchValue("");
              setSearchOptions([]);
            }}
          />
        </AutoComplete>

        <div className="nav-icons">
          <span className="icon">
            <FaBell />
          </span>

          {/* Profile Dropdown */}
          <div className="profile-dropdown-container" ref={dropdownRef}>
            <span className="icon profile-icon" onClick={toggleProfileDropdown}>
              <FaUserCircle style={{ imageRendering: "auto" }} />
              <FaChevronDown className={`dropdown-arrow ${showProfileDropdown ? "rotated" : ""}`} />
            </span>

            {showProfileDropdown && (
              <div className="profile-dropdown">
                <div className="profile-header">
                  <FaUserCircle className="profile-avatar" />
                  <div className="profile-info">
                    <div className="profile-name">{userDetails.loading ? "Loading..." : userDetails.name}</div>
                    <div className="profile-email">{userDetails.loading ? "Loading..." : userDetails.email}</div>
                  </div>
                </div>

                <div className="profile-details">
                  {userDetails.phoneNumber && (
                    <div className="profile-detail-item">
                      <span className="detail-label">Phone Number:</span>
                      <span className="detail-value">{userDetails.loading ? "Loading..." : userDetails.phoneNumber}</span>
                    </div>
                  )}
                  <div className="profile-detail-item">
                    <span className="detail-label">Date of Birth:</span>
                    <span className="detail-value">{userDetails.loading ? "Loading..." : userDetails.dateOfBirth}</span>
                  </div>
                  <div className="profile-detail-item">
                    <span className="detail-label">Role:</span>
                    <span className="detail-value">{userDetails.loading ? "Loading..." : userDetails.role}</span>
                  </div>
                </div>

                <div className="profile-actions">
                  <button className="logout-btn" onClick={handleLogout}>
                    <FaSignOutAlt />
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TopNavBar;
